import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Brain,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ClipboardList,
  Download,
  Send,
  MessageCircle,
} from 'lucide-react';

const AnalysisTab = ({
  analyzing,
  latestAnalysis,
  chatMessage,
  setChatMessage,
  chatSending,
  chatEndRef,
  handleChatSend,
  handleExportChat,
  setActiveTab,
}) => {
  if (analyzing) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#4B6651]/10 rounded-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#4B6651]" />
          </div>
          <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
            Analyzing Neurological Findings
          </h3>
          <p className="text-[#5C5F5C]">
            AI is generating lesion localization, rule-outs, and diagnostic recommendations...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!latestAnalysis) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#F8F7F4] rounded-full flex items-center justify-center">
            <Brain className="w-8 h-8 text-[#5C5F5C]" />
          </div>
          <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
            No Analysis Available
          </h3>
          <p className="text-[#5C5F5C] mb-6">
            Complete a neurological examination to generate AI-powered lesion localization and diagnostic recommendations.
          </p>
          <Button
            onClick={() => setActiveTab('exam')}
            className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Start Neurological Exam
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-[#E5E4E0] bg-white" data-testid="localization-card">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#4B6651]" />
            Lesion Localization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-3">
                Abnormal Findings
              </p>
              {latestAnalysis.rule_based_localization?.abnormal_findings?.length > 0 ? (
                <div className="space-y-2">
                  {latestAnalysis.rule_based_localization.abnormal_findings.map((finding, idx) => (
                    <div key={`detail-finding-${finding.slice(0,20)}-${idx}`} className="flex items-start gap-2 p-2 bg-[#C06144]/10 rounded">
                      <AlertTriangle className="w-4 h-4 text-[#C06144] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#2A2C2A]">{finding}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-[#4A7C59]/10 rounded">
                  <CheckCircle className="w-4 h-4 text-[#4A7C59]" />
                  <span className="text-sm text-[#2A2C2A]">No significant abnormalities detected</span>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-3">
                Possible Lesion Locations
              </p>
              {latestAnalysis.rule_based_localization?.possible_locations?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {latestAnalysis.rule_based_localization.possible_locations.map((loc) => (
                    <span key={`detail-loc-${loc}`} className="px-3 py-1.5 bg-[#4B6651] text-white rounded-full text-sm font-medium">
                      {loc}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[#5C5F5C]">Unable to localize based on current findings</p>
              )}

              {latestAnalysis.rule_based_localization?.lateralization && (
                <div className="mt-4">
                  <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-2">
                    Lateralization
                  </p>
                  <span className="px-3 py-1.5 bg-[#F8F7F4] text-[#2A2C2A] rounded text-sm">
                    {latestAnalysis.rule_based_localization.lateralization}
                  </span>
                </div>
              )}

              {latestAnalysis.rule_based_localization?.umn_lmn_pattern && (
                <div className="mt-4">
                  <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-2">
                    UMN/LMN Pattern
                  </p>
                  <span className="px-3 py-1.5 bg-[#F8F7F4] text-[#2A2C2A] rounded text-sm">
                    {latestAnalysis.rule_based_localization.umn_lmn_pattern}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E4E0] bg-white" data-testid="ai-analysis-card">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C06144]" />
            AI-Powered Analysis
            <span className="ml-2 px-2 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded text-xs font-medium">
              GPT-5.2
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-[#2A2C2A] leading-relaxed">
              {latestAnalysis.ai_analysis?.replace(/\*\*/g, '')}
            </div>
          </div>

          {latestAnalysis.chat_messages?.length > 0 && (
            <div className="mt-6 border-t border-[#E5E4E0] pt-4 space-y-3" data-testid="chat-messages">
              <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" />
                Follow-up Discussion
              </p>
              {latestAnalysis.chat_messages.map((msg, idx) => (
                <div
                  key={`chat-${msg.created_at}-${idx}`}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-msg-${idx}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-[#4B6651] text-white'
                        : 'bg-[#F8F7F4] text-[#2A2C2A] border border-[#E5E4E0]'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{msg.content?.replace(/\*\*/g, '')}</div>
                    <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/60' : 'text-[#5C5F5C]'}`}>
                      {msg.role === 'user' ? 'You' : 'AI Neurologist'} · {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {chatSending && (
                <div className="flex justify-start" data-testid="chat-loading">
                  <div className="bg-[#F8F7F4] border border-[#E5E4E0] rounded-lg px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#4B6651]" />
                    <span className="text-sm text-[#5C5F5C]">AI is thinking...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}

          <div className="mt-6 border-t border-[#E5E4E0] pt-4" data-testid="chat-input-area">
            <p className="text-xs text-[#5C5F5C] mb-2">
              {latestAnalysis.chat_messages?.length > 0
                ? 'Continue discussing this case with the AI neurologist'
                : 'Have questions about this analysis? Ask the AI neurologist'}
            </p>
            <div className="flex gap-2">
              <Textarea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSend();
                  }
                }}
                placeholder="Type your response or question..."
                className="flex-1 min-h-[44px] max-h-32 resize-none border-[#E5E4E0] focus:border-[#4B6651] text-sm"
                disabled={chatSending}
                data-testid="chat-input"
              />
              <Button
                onClick={handleChatSend}
                disabled={!chatMessage.trim() || chatSending}
                className="bg-[#4B6651] hover:bg-[#3E5543] text-white px-3 self-end"
                data-testid="chat-send-btn"
              >
                {chatSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            {latestAnalysis.chat_messages?.length > 0 && (
              <div className="mt-3 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportChat}
                  className="text-xs border-[#E5E4E0] text-[#5C5F5C] hover:text-[#2A2C2A] hover:border-[#4B6651]"
                  data-testid="export-chat-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Export Chat as PDF
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalysisTab;
